import React, { createContext, useContext, useMemo, useState } from 'react';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addProduct = (piece) => {
    setItems((current) => {
      // Pieces are one-of-a-kind - don't let the same item get added twice.
      if (current.some((line) => line.kind === 'product' && line.id === piece.id)) {
        return current;
      }
      return [
        ...current,
        {
          key: `product-${piece.id}`,
          kind: 'product',
          id: piece.id,
          variationId: piece.variationId,
          name: piece.title,
          meta: piece.description,
          price: piece.price,
          imageUrl: piece.imageUrl ?? null,
        },
      ];
    });
    setIsOpen(true);
  };

  const addLesson = ({ service, date, time, startAt, teamMemberId, partySize }) => {
    const key = `lesson-${service.id}-${date}-${time}`;
    setItems((current) => {
      if (current.some((line) => line.key === key)) {
        return current;
      }
      return [
        ...current,
        {
          key,
          kind: 'lesson',
          id: service.id,
          variationId: service.id,
          variationVersion: service.version,
          teamMemberId,
          startAt,
          durationMinutes: service.durationMinutes,
          name: service.name,
          meta: `${date} at ${time} · ${partySize} ${partySize === 1 ? 'person' : 'people'}`,
          price: service.price,
          partySize,
          imageUrl: null,
        },
      ];
    });
    setIsOpen(true);
  };

  const removeItem = (key) => {
    setItems((current) => current.filter((line) => line.key !== key));
  };

  const clearCart = () => setItems([]);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  const subtotal = useMemo(
    () => items.reduce((sum, line) => sum + (line.price ?? 0), 0),
    [items]
  );

  const value = {
    items,
    isOpen,
    addProduct,
    addLesson,
    removeItem,
    clearCart,
    openCart,
    closeCart,
    subtotal,
    count: items.length,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
